import { AlertRange, Daycare, DaycareType } from '@/types/Daycare'
import moment, { Moment } from 'moment-timezone'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import {
    DAYTIME_FORMAT,
    HOUR_MINUTE_FORMAT,
    HOUR_MINUTE_SECOND_FORMAT
} from '@/container/planning/Dialog/occasionalSlot/models/constants'

export function daycareTypeToString(
    daycareType: DaycareType,
    t: (key: string) => string,
    short: boolean = false
): string {
    switch (daycareType) {
        case DaycareType.nursery:
            return t('nursery')
        case DaycareType.daycare:
            return t('daycare')
        case DaycareType.multiHome:
            return t('multiHome')
        case DaycareType.recreationCenter:
            return t('recreationCenter')
        case DaycareType.playCenter:
            return t('playCenter')
        case DaycareType.microDaycare:
            return t('microDaycare')
        case DaycareType.toyLibrary:
            return t('toyLibrary')
        case DaycareType.rpe:
            return short ? t('shortRpe') : t('rpe')
        case DaycareType.laep:
            return short ? t('shortLaep') : t('laep')
        default:
            return t('other')
    }
}

type MomentForCheckOpening = { moment: Moment } | { day: string; time: string }

export function isDaycareOpenOnMoment(daycare: Daycare, date: MomentForCheckOpening): boolean {
    const fullDate: Moment = moment('moment' in date ? date.moment.clone() : date.day, 'DD-MM-YYYY HH:mm')

    if ('day' in date && 'time' in date) {
        const time: Moment = moment(date.time, HOUR_MINUTE_FORMAT)
        fullDate.set({ hour: time.hour(), minute: time.minute() })
    }

    const day: number = fullDate.isoWeekday() - 1
    const isDaycareOpenOnDay: boolean = isDaycareOpenThisDay(daycare, { moment: fullDate })

    const daycareOpening: string | null = getDaycareOpening(daycare, day)
    const daycareClosing: string | null = getDaycareClosing(daycare, day)

    if (!daycareOpening || !daycareClosing) return false
    const hoursAndMinutes: string = fullDate.format(HOUR_MINUTE_FORMAT)

    return isDaycareOpenOnDay && hoursAndMinutes >= daycareOpening && hoursAndMinutes <= daycareClosing
}

type MomentForCheckDailyOpening = { moment: Moment } | { day: string }

export function isDaycareOpenThisDay(daycare: Daycare, date: MomentForCheckDailyOpening): boolean {
    const fullDate: Moment = moment('moment' in date ? date.moment.clone() : date.day, DAYTIME_FORMAT)
    const day: number = fullDate.isoWeekday() - 1
    return getDaycareOpening(daycare, day) !== null && getDaycareClosing(daycare, day) !== null
}

export function getDaycareOpening(daycare: Daycare, day: number): string | null {
    switch (day) {
        case 0:
            return daycare.opening.monOpen
                ? moment(daycare.opening.monOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 1:
            return daycare.opening.tueOpen
                ? moment(daycare.opening.tueOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 2:
            return daycare.opening.wedOpen
                ? moment(daycare.opening.wedOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 3:
            return daycare.opening.thuOpen
                ? moment(daycare.opening.thuOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 4:
            return daycare.opening.friOpen
                ? moment(daycare.opening.friOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 5:
            return daycare.opening.satOpen
                ? moment(daycare.opening.satOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 6:
            return daycare.opening.sunOpen
                ? moment(daycare.opening.sunOpen, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        default:
            return null
    }
}

export function getDaycareClosing(daycare: Daycare, day: number): string | null {
    switch (day) {
        case 0:
            return daycare.opening.monClose
                ? moment(daycare.opening.monClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 1:
            return daycare.opening.tueClose
                ? moment(daycare.opening.tueClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 2:
            return daycare.opening.wedClose
                ? moment(daycare.opening.wedClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 3:
            return daycare.opening.thuClose
                ? moment(daycare.opening.thuClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 4:
            return daycare.opening.friClose
                ? moment(daycare.opening.friClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 5:
            return daycare.opening.satClose
                ? moment(daycare.opening.satClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        case 6:
            return daycare.opening.sunClose
                ? moment(daycare.opening.sunClose, HOUR_MINUTE_SECOND_FORMAT).format(HOUR_MINUTE_FORMAT)
                : null
        default:
            return null
    }
}

export function getDaycareLastOpening(daycare: Daycare): string {
    const moments: Moment[] = [
        moment(daycare.opening.monOpen, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.tueOpen, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.wedOpen, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.thuOpen, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.friOpen, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.satOpen, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.sunOpen, HOUR_MINUTE_SECOND_FORMAT)
    ].filter((moment: Moment) => moment.isValid())

    return moments.length > 0 ? moment.min(moments).format(HOUR_MINUTE_FORMAT) : '08:00'
}

export function getDaycareLastClosing(daycare: Daycare): string {
    const moments: Moment[] = [
        moment(daycare.opening.monClose, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.tueClose, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.wedClose, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.thuClose, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.friClose, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.satClose, HOUR_MINUTE_SECOND_FORMAT),
        moment(daycare.opening.sunClose, HOUR_MINUTE_SECOND_FORMAT)
    ].filter((moment: Moment) => moment.isValid())

    return moments.length > 0 ? moment.max(moments).format(HOUR_MINUTE_FORMAT) : '18:00'
}

export function getNextOpeningDate(daycare: Daycare, date: Moment): Moment {
    const dateCheck: Moment = date.clone()

    if (getDaycareDaysOpening(daycare).length === 0) {
        return date
    }

    while (
        getDaycareOpening(daycare, (dateCheck.day() + 6) % 7) === null &&
        getDaycareClosing(daycare, (dateCheck.day() + 6) % 7) === null
    ) {
        dateCheck.add(1, 'd')
    }

    return dateCheck
}

export function getPreviousOpeningDate(daycare: Daycare, date: Moment): Moment {
    const dateCheck: Moment = date.clone()

    if (getDaycareDaysOpening(daycare).length === 0) {
        return date
    }

    while (
        getDaycareOpening(daycare, (dateCheck.day() + 6) % 7) === null &&
        getDaycareClosing(daycare, (dateCheck.day() + 6) % 7) === null
    ) {
        dateCheck.subtract(1, 'd')
    }

    return dateCheck
}

export function getDaycareWeekClosingDay(daycare: Daycare): number[] {
    const closingDay: number[] = []

    if (!daycare.opening.monClose && !daycare.opening.monOpen) {
        closingDay.push(1)
    }
    if (!daycare.opening.tueClose && !daycare.opening.tueOpen) {
        closingDay.push(2)
    }
    if (!daycare.opening.wedClose && !daycare.opening.wedOpen) {
        closingDay.push(3)
    }
    if (!daycare.opening.thuClose && !daycare.opening.thuOpen) {
        closingDay.push(4)
    }
    if (!daycare.opening.friClose && !daycare.opening.friOpen) {
        closingDay.push(5)
    }
    if (!daycare.opening.satClose && !daycare.opening.satOpen) {
        closingDay.push(6)
    }
    if (!daycare.opening.sunClose && !daycare.opening.sunOpen) {
        closingDay.push(0)
    }

    return closingDay
}

export function alertRangeToNumber(daycare: Daycare): number {
    switch (daycare.alertRange) {
        case AlertRange.alert24:
            return 24
        case AlertRange.alert48:
            return 48
        case AlertRange.alert72:
            return 72
        case AlertRange.alert7d:
            return 7 * 24
        case AlertRange.alert14d:
            return 14 * 24
        default:
            return 0
    }
}

export function getDaycareDaysOpening(daycare: Daycare): number[] {
    const openingDays: number[] = []

    if (daycare.opening.monOpen && daycare.opening.monClose) {
        openingDays.push(0)
    }
    if (daycare.opening.tueOpen && daycare.opening.tueClose) {
        openingDays.push(1)
    }
    if (daycare.opening.wedOpen && daycare.opening.wedClose) {
        openingDays.push(2)
    }
    if (daycare.opening.thuOpen && daycare.opening.thuClose) {
        openingDays.push(3)
    }
    if (daycare.opening.friOpen && daycare.opening.friClose) {
        openingDays.push(4)
    }
    if (daycare.opening.satOpen && daycare.opening.satClose) {
        openingDays.push(5)
    }
    if (daycare.opening.sunOpen && daycare.opening.sunClose) {
        openingDays.push(6)
    }

    return openingDays
}

export function getDaycareDaysSelectOptions(daycare: Daycare, t: (key: string) => string): SelectOptionsDeprecated {
    return [
        {
            options: [
                daycare?.opening.monOpen && daycare?.opening.monClose
                    ? { value: '0', name: t('monday') }
                    : { value: '', name: '' },
                daycare?.opening.tueOpen && daycare?.opening.tueClose
                    ? { value: '1', name: t('tuesday') }
                    : { value: '', name: '' },
                daycare?.opening.wedOpen && daycare?.opening.wedClose
                    ? { value: '2', name: t('wednesday') }
                    : { value: '', name: '' },
                daycare?.opening.thuOpen && daycare?.opening.thuClose
                    ? { value: '3', name: t('thursday') }
                    : { value: '', name: '' },
                daycare?.opening.friOpen && daycare?.opening.friClose
                    ? { value: '4', name: t('friday') }
                    : { value: '', name: '' },
                daycare?.opening.satOpen && daycare?.opening.satClose
                    ? { value: '5', name: t('saturday') }
                    : { value: '', name: '' },
                daycare?.opening.sunOpen && daycare?.opening.sunClose
                    ? { value: '6', name: t('sunday') }
                    : { value: '', name: '' }
            ].filter((opening: SelectOptionDeprecated) => opening.value != '' && opening.name != '')
        }
    ]
}
